import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import noop from 'lodash/noop';
import { FormattedMessage } from 'react-intl';
import checkIsMobile from 'shared/utils/checkMobileDevice';

import { Icon } from 'shared/components';

import theme from './ToastMessage.scss';

const ToastMessage = ({
  testId,
  children,
  rtl,
  showClose,
  onClose,
  closeText,
  showRetry,
  onRetry,
  showUndo,
  onAction,
  onUndo,
  style,
  hasDuplicate,
  autoDismissTime,
  type,
}) => {
  const interval = 1000;
  const transitionDuration = autoDismissTime / 1000;
  const [counter, setCounter] = useState(autoDismissTime);
  const isMobile = checkIsMobile();

  useEffect(() => {
    if (!autoDismissTime) return;

    const needle = setTimeout(() => {
      const nextStep = counter - interval;
      setCounter(nextStep);

      if (!nextStep) {
        onClose();
      }
    }, interval);

    return () => {
      if (counter) return clearInterval(needle);
    };
  }, [counter]);

  return (
    <div
      data-testid={ testId }
      key={ hasDuplicate }
      style={ style }
      className={ classnames(theme.root, theme[type], {
        rtl,
        [theme.shake]: hasDuplicate,
        [theme.withAction]: !!onAction,
        [theme.mobile]: isMobile,
      }) }
    >
      <div className={ theme.iconContainer }>
        <Choose>
          <When condition={ type === 'info' }>
            <Icon name="exclamation" />
          </When>
          <When condition={ type === 'failure' }>
            <Icon name="naked-x" />
          </When>
          <When condition={ type === 'success' }>
            <Icon name="check" />
          </When>
          <When condition={ type === 'enforcedQueueUpdate' }>
            <Icon name="characters-shrink" />
          </When>
        </Choose>
      </div>
      <div className={ theme.content }>
        { children }
      </div>
      <div className={ theme.buttonsWrap }>
        <If condition={ !!onAction } >
          <button className={ theme.button } onClick={ () => { onAction(); onClose(); } }>
            <FormattedMessage id="Admin.Appointment.Handoff.ApptCreated.Snackbar.ViewButton" defaultMessage="** view" />
          </button>
        </If>
        <Choose>
          <When condition={ showRetry }>
            <button className={ theme.button } onClick={ onRetry }>
              <FormattedMessage id="AdminConsole.Appointments.DetailedView.AppointmentOutputs.FailureSnackbar.Retry" defaultMessage="** retry" />
            </button>
          </When>
          <When condition={ showClose }>
            <button className={ theme.button } onClick={ onClose }>
              <FormattedMessage { ...closeText } />
            </button>
          </When>
          <When condition={ showUndo }>
            <button className={ theme.button } onClick={ onUndo }>
              <FormattedMessage id="General.Undo" defaultMessage="** undo" />
            </button>
          </When>
        </Choose>
        <If condition={ autoDismissTime && !onAction }>
          <div className={ theme.counter }>
            <div className={ theme.progressWrap }>
              <div className={ theme.circle }>
                <div className={ theme.mask } style={{ animationDuration: `${ transitionDuration }s` }}>
                  <div className={ theme.fill } style={{ animationDuration: `${ transitionDuration }s` }} />
                </div>
                <div className={ theme.mask }>
                  <div className={ theme.fill } style={{ animationDuration: `${ transitionDuration }s` }} />
                </div>
                <div className={ theme.insideCircle }>
                  { counter / 1000 }
                </div>
              </div>
            </div>
          </div>
        </If>
      </div>
    </div>
  );
};

ToastMessage.propTypes = {
  testId: PropTypes.string,
  style: PropTypes.object,
  /** children content that will be displayed in toast */
  children: PropTypes.node.isRequired,
  /** define true for languages that write from right to left */
  rtl: PropTypes.bool,
  /** set to true if you want to display close button */
  showClose: PropTypes.bool,
  /** function that will be run after click on close button */
  onClose: PropTypes.func,
  /** text to be placed in the button **/
  closeText: PropTypes.object,
  /** set to true if you want to display retry button */
  showRetry: PropTypes.bool,
  /** function that will be run after click on retry button */
  onRetry: PropTypes.func,

  showUndo: PropTypes.bool,
  onUndo: PropTypes.func,

  onAction: PropTypes.func,

  /** whether there is already such toast, triggers shaking of existing one */
  hasDuplicate: PropTypes.number,
  /** Visual type of snackbar */
  type: PropTypes.oneOf(['info', 'failure', 'success', 'enforcedQueueUpdate']),
  autoDismissTime: PropTypes.number,
};

ToastMessage.defaultProps = {
  testId: null,
  style: undefined,
  rtl: false,
  onClose: noop,
  showClose: true,
  closeText: {
    id: 'General.Close',
    defaultMessage: '** close',
  },
  onRetry: noop,
  showRetry: false,
  showUndo: false,
  onUndo: noop,
  onAction: null,
  hasDuplicate: 0,
  type: 'info',
  autoDismissTime: 0,
};

export default ToastMessage;
